import React from 'react';
import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { supabase } from './lib/supabase';
import Auth from './components/Auth';
import Home from './pages/Home';
import Overview from './pages/Overview';
import Tasks from './pages/Tasks';
import Projects from './pages/Projects';
import Customers from './pages/Agencies';
import Employees from './pages/Employees';
import AIAgents from './pages/AIAgents';
import AgentOutputs from './pages/AgentOutputs';
import Prompts from './pages/Prompts';
import TaskDetailsPage from './pages/TaskDetails';
import Research from './pages/Research';
import Footer from './components/Footer';
import DailyPlanner from './pages/DailyPlanner';
import SuperAdmin from './pages/SuperAdmin';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import CookiesPolicy from './pages/CookiesPolicy';
import MyAccount from './pages/MyAccount';
import Notes from './pages/Notes';
import Settings from './pages/Settings';
import PlanInfo from './pages/AccountType';
import Reports from './pages/Reports';
import CustomReportPage from './pages/CustomReportPage';
import UserAccounts from './pages/UserAccounts';
import { User } from '@supabase/supabase-js';
import ScrollToTop from './components/ScrollToTop';
import { clearAuthData, refreshSession } from './lib/auth';

function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [path, setPath] = useState(window.location.pathname);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Define public pages that don't require authentication
  const publicPages = ['/privacy-policy', '/terms-and-conditions', '/cookies-policy', '/faq'];

  // Handle navigation
  const navigate = (to: string) => {
    window.history.pushState({}, '', to);
    setPath(to);
  };

  // Listen for popstate events (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const initSession = async () => {
      try {
        setIsInitialized(false);
        setIsLoading(true);

        // Try to refresh session first
        const session = await refreshSession();
        
        if (session?.user) {
          setUser(session.user);
          if (path === '/auth') {
            navigate('/overview');
          }
        } else {
          setUser(null);
          clearAuthData();
          // Only redirect from non-public pages
          if (path !== '/' && !publicPages.includes(path)) {
            navigate('/');
          }
        }

      } catch (error) {
        console.error('Session initialization error:', error);
        setUser(null);
        clearAuthData();
        // Don't redirect from public pages
        if (!publicPages.includes(path)) {
          navigate('/');
        }
      } finally {
        setIsInitialized(true);
        setIsLoading(false);
      }
    };

    initSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (_event === 'SIGNED_OUT' || _event === 'USER_DELETED') {
        clearAuthData();
        setUser(null);
        // Don't redirect from public pages
        if (!publicPages.includes(path)) {
          navigate('/');
        }
        return;
      }

      if (session?.user) {
        setUser(session.user);
        if (path === '/auth') {
          navigate('/overview');
        }
      } else {
        setUser(null);
        clearAuthData();
        // Don't redirect from public pages
        if (path !== '/' && !publicPages.includes(path)) {
          navigate('/');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (!isInitialized || isLoading || user === undefined) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Allow access to policy pages without login
  // This section renders policy pages for both authenticated and unauthenticated users
  if (publicPages.includes(path)) {
    return (
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen bg-white flex flex-col">
          {path === '/privacy-policy' && <PrivacyPolicy navigate={navigate} />}
          {path === '/terms-and-conditions' && <TermsAndConditions navigate={navigate} />}
          {path === '/cookies-policy' && <CookiesPolicy navigate={navigate} />}
          <Footer navigate={navigate} />
        </div>
      </BrowserRouter>
    );
  }

  // Show auth page if on /auth path and not authenticated
  if (path === '/auth') {
    return user ? (
      navigate('/overview')
    ) : (
      <Auth />
    );
  }

  // Show home page for unauthenticated users on non-auth paths
  if (!user) {
    // Show home page
    return (
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen bg-white flex flex-col">
          <Home navigate={navigate} />
          <Footer navigate={navigate} />
        </div>
      </BrowserRouter>
    );
  }

  // Redirect authenticated users from root to overview
  if (path === '/') {
    return navigate('/overview');
  }

  // Get the base component for the current path
  const getComponent = () => {
    switch (path) {
      case '/projects': return <Projects navigate={navigate} />;
      case '/overview': return <Overview navigate={navigate} />;
      case '/employees': return <Employees navigate={navigate} />;
      case '/customers': return <Customers navigate={navigate} />;
      case '/my-account': return <MyAccount navigate={navigate} />;
      case '/settings': return <Settings navigate={navigate} />;
      case '/research': return <Research navigate={navigate} />;
      case '/tasks': return <Tasks navigate={navigate} />;
      case '/notes': return <Notes navigate={navigate} />;
      case '/ai-agents': return <AIAgents navigate={navigate} />;
      case '/agent-outputs': return <AgentOutputs navigate={navigate} />;
      case '/prompts': return <Prompts navigate={navigate} />;
      case '/daily-planner': return <DailyPlanner navigate={navigate} />;
      case '/reports': return <Reports navigate={navigate} />;
      case '/reports/create': return <CustomReportPage navigate={navigate} />;
      case '/user-accounts': return <UserAccounts navigate={navigate} />;
      case '/account-type': return <PlanInfo navigate={navigate} />;
      case '/super-admin': return <SuperAdmin navigate={navigate} />;
      default:
        // Handle report edit path
        if (path.startsWith('/reports/edit/')) {
          const reportId = path.split('/reports/edit/')[1];
          return <CustomReportPage navigate={navigate} reportId={reportId} />;
        }
        // Handle task details path
        if (path.startsWith('/tasks/')) {
          return <TaskDetailsPage navigate={navigate} />;
        }
        // Redirect to overview for unknown paths
        return <Overview navigate={navigate} />;
    }
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="min-h-screen bg-white flex flex-col">
        {getComponent()}
        <Footer navigate={navigate} />
      </div>
    </BrowserRouter>
  );
}

export default App;